/**
 * Turning a dashboards workshop entry into a calendar chip (BON-534).
 *
 * The API and the calendar disagree on two field names (`treeSpecies` against
 * `species`) and the calendar requires a venue abbreviation and an accent
 * colour the API treats as optional. This is the one place that reconciles
 * them.
 *
 * Lifted out of `RegionalOverviewDesktop`, which held the only copy — the
 * unified Overview needs the same mapping, and that file goes away with the old
 * per-persona pages.
 */
import type { WorkshopChip } from '../primitives';
import type { RegionalWorkshopEntry } from '../regional/regionalApi';

const FALLBACK_PALETTE = ['#0e874b', '#cc6726', '#257bea', '#ba7517', '#7a5ae0'];

/** "Austin Beerworks Sprinkle" → "ABS". Used when the server sends no short name. */
export function fallbackVenueShort(name: string): string {
  return name
    .split(/\s+/)
    .map((word) => word[0])
    .filter(Boolean)
    .slice(0, 3)
    .join('')
    .toUpperCase();
}

/**
 * A stable colour for a seed.
 *
 * The design hard-codes one colour per metro; real data has an arbitrary number
 * of sub-regions at arbitrary depth, so the colour is derived instead. Seed it
 * with the SUB-REGION wherever there is one (see `accentFor`) — the colour is
 * meant to say which territory a workshop belongs to, and the filter chips key
 * off the same value.
 */
export function fallbackAccent(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  return FALLBACK_PALETTE[Math.abs(hash) % FALLBACK_PALETTE.length];
}

/**
 * The colour to draw a workshop in: the server's if it sent one, otherwise one
 * derived from its sub-region. Only a workshop with NO sub-region falls back to
 * its venue, which keeps unassigned venues distinguishable instead of collapsing
 * them all onto one colour. `venueId` is null for a venueless private event, so
 * that case falls back once more, to the workshop's own id — never to a bare
 * empty/null seed, which would crash `fallbackAccent` and collapse every such
 * event onto the same colour besides.
 */
export function accentFor(w: {
  id: string;
  accentColor?: string | null;
  subregionLabel?: string | null;
  venueId: string | null;
}): string {
  if (w.accentColor) return w.accentColor;
  return fallbackAccent(w.subregionLabel || w.venueId || w.id);
}

export function toWorkshopChips(workshops: readonly RegionalWorkshopEntry[]): WorkshopChip[] {
  return workshops.map((w) => ({
    id: w.id,
    date: w.date,
    localDate: w.localDate,
    venueName: w.venueName,
    venueShort: w.venueShort ?? fallbackVenueShort(w.venueName),
    species: w.treeSpecies,
    capacity: w.capacity,
    booked: w.booked,
    leadInstructor: w.leadInstructor,
    assistant: w.assistant,
    accentColor: accentFor(w),
    subregionLabel: w.subregionLabel,
    regionSlug: w.regionSlug,
    lifecycle: w.lifecycle,
    status: w.status,
    replacements: w.replacements,
    mine: Boolean(w.isMine),
    myRole: w.myRole,
    isPrivate: w.isPrivate,
  }));
}
